<div class="kv-scroll p-2 p-sm-3">
  <!-- What this is, and how to check a mapping by hand -->
  <div class="d-flex align-items-start gap-2 mb-2">
    <div class="kv-hint me-auto">
      🔒 {{ 'ADMIN_KNX_VALVES_INTRO' | translate }}
      <details class="mt-1">
        <summary>{{ 'ADMIN_KNX_VALVES_HOW' | translate }}</summary>
        <span class="d-block mt-1">{{ 'ADMIN_KNX_VALVES_VERIFIED_TEXT' | translate }}</span>
        <span class="d-block mt-1">{{ 'ADMIN_KNX_VALVES_HOW_TEXT' | translate }}</span>
        <span class="d-block mt-1">{{ 'ADMIN_KNX_VALVES_PUBLIC' | translate }}<ng-container *ngIf="thresholdText"> {{ thresholdText }}</ng-container></span>
      </details>
    </div>
    <div class="d-flex align-items-center gap-2 flex-shrink-0">
      <span *ngIf="fetchedTime" class="kv-hint text-nowrap d-none d-sm-inline">{{ 'ADMIN_KNX_VALVES_AS_OF' | translate:{ time: fetchedTime } }}</span>
      <button type="button" class="btn btn-sm btn-outline-secondary m-touch-sm" (click)="refresh()"
              [title]="'AKTUALISIEREN' | translate" [attr.aria-label]="'AKTUALISIEREN' | translate">↻</button>
    </div>
  </div>
  <!-- The actuator table (read from the actuators): how much of it applies, and what does not (falls back to book / learning) -->
  <div *ngIf="tableText" class="kv-hint mb-2" [title]="tableTitle">
    {{ tableText }}
    <ul *ngIf="tableSkips.length" class="kv-reasons text-warning-emphasis">
      <li *ngFor="let x of tableSkips">{{ x }}</li>
    </ul>
  </div>
  <div class="kv-test mb-3">🧪 {{ 'ADMIN_KNX_VALVES_TEST_HINT' | translate }}</div>

  <div *ngIf="error" class="alert alert-danger py-1 px-2 small">{{ 'ADMIN_KNX_ERROR' | translate:{ msg: error } }}</div>
  <div *ngIf="backendError" class="alert alert-warning py-1 px-2 small" style="overflow-wrap:anywhere;">⚠ {{ backendError }}</div>
  <div *ngIf="!loaded" class="text-muted small text-center py-4">{{ 'ADMIN_KNX_LOADING' | translate }}</div>
  <div *ngIf="loaded && !error && !blocks.length" class="text-muted small text-center py-4 px-3">{{ 'ADMIN_KNX_VALVES_EMPTY' | translate }}</div>

  <section *ngFor="let b of blocks; trackBy: trackBlock" class="kv-block">
    <!-- Floor block: which actuator serves which floor, and why -->
    <div class="kv-head">
      <div class="d-flex flex-wrap align-items-baseline" style="gap:.1rem .5rem;">
        <span class="fw-semibold">{{ b.floorLabel || ('ADMIN_KNX_VALVES_NO_FLOOR' | translate) }}</span>
        <span *ngIf="b.actuator" class="mono kv-head-sub">{{ 'ADMIN_KNX_VALVES_ACTUATOR' | translate:{ ia: b.actuator } }}</span>
        <span *ngIf="b.group" class="mono kv-head-sub">{{ b.group }}</span>
        <span class="kv-head-sub">{{ b.facts }}</span>
      </div>
      <div *ngIf="b.ambiguous" class="kv-head-sub text-warning-emphasis mt-1">⚠ {{ 'ADMIN_KNX_VALVES_AMBIGUOUS' | translate }}</div>
      <ul *ngIf="b.reasons.length" class="kv-reasons">
        <li *ngFor="let r of b.reasons">{{ r }}</li>
      </ul>
    </div>

    <!-- Rooms of the block: Ist / Soll and the valve they got (a room more than 0.5° below Soll is outlined) -->
    <div class="kv-rooms">
      <span *ngFor="let r of b.rooms; trackBy: trackRoom" class="kv-room" [class.need]="r.need" [class.none]="!r.valveGa"
            [title]="r.need ? ('ADMIN_KNX_VALVES_NEEDS_HEAT' | translate) : ''">
        <span class="fw-semibold">{{ r.label }}</span>
        <ng-container *ngIf="r.ist"> · {{ r.ist }}</ng-container><ng-container *ngIf="r.soll"> / {{ r.soll }}</ng-container><span
          *ngIf="r.delta" [class.text-primary-emphasis]="r.need" [class.fw-semibold]="r.need"> ({{ r.delta }})</span>
        · <span class="mono">{{ r.valveGa || ('ADMIN_KNX_VALVES_NO_VALVE' | translate) }}</span>
      </span>
      <span *ngIf="!b.rooms.length" class="kv-hint">{{ 'ADMIN_KNX_VALVES_NO_ROOMS' | translate }}</span>
    </div>

    <!-- Valves -->
    <div class="kv-row kv-th">
      <span>{{ 'ADMIN_KNX_COL_GA' | translate }}</span>
      <span>{{ 'ADMIN_KNX_VALVES_COL_ACTUATOR' | translate }}</span>
      <span>{{ 'ADMIN_KNX_VALVES_COL_NOW' | translate }}</span>
      <span>{{ 'ADMIN_KNX_VALVES_COL_ROOM' | translate }}</span>
      <span>{{ 'ADMIN_KNX_VALVES_COL_CANDIDATES' | translate }}</span>
      <span>{{ 'ADMIN_KNX_VALVES_COL_ACTIONS' | translate }}</span>
    </div>
    <div *ngFor="let v of b.valves; trackBy: trackValve" class="kv-row">
      <div class="kv-c-ga">
        <span class="mono fw-semibold">{{ v.ga }}</span>
        <div *ngIf="v.bookName" class="kv-sub" [title]="'ADMIN_KNX_VALVES_BOOK_NAME' | translate">{{ v.bookName }}</div>
      </div>
      <!-- Actuator / channel: for a valve read from the actuator its channel (A…F) and the Ist GA of that channel -->
      <div class="kv-c-act" [title]="v.verifiedTitle">
        <span class="mono">{{ v.actuator || '–' }}</span><span *ngIf="v.pwm" class="badge kv-pwm" [title]="'ADMIN_KNX_VALVES_PWM_HINT' | translate">PWM</span>
        <div *ngIf="v.channel" class="kv-sub">{{ v.channel }}</div>
        <div *ngIf="v.istGa" class="kv-sub mono">{{ v.istGa }}</div>
      </div>
      <div class="kv-c-pct" [title]="v.agoFull">
        <span class="kv-pct">{{ v.pctText }}</span>
        <div class="kv-gauge"><span [style.width.%]="v.pct ?? 0"></span></div>
        <div *ngIf="v.ago" class="kv-sub text-nowrap">{{ v.ago }}</div>
      </div>
      <div class="kv-c-room">
        <span class="kv-lbl">{{ 'ADMIN_KNX_VALVES_COL_ROOM' | translate }}</span>
        <ng-container *ngIf="v.roomId; else noRoom">
          <span class="fw-semibold">{{ v.roomLabel }}</span>
        </ng-container>
        <ng-template #noRoom><span class="text-muted">{{ 'ADMIN_KNX_VALVES_UNASSIGNED' | translate }}</span></ng-template>
        <span *ngIf="v.status" class="badge kv-st" [ngClass]="'kv-st-' + v.status"
              [title]="('ADMIN_KNX_VALVES_ST_' + v.status.toUpperCase() + '_HINT') | translate">{{ ('ADMIN_KNX_VALVES_ST_' + v.status.toUpperCase()) | translate }}</span>
        <div *ngIf="v.learnedLabel" class="kv-sub">{{ 'ADMIN_KNX_VALVES_LEARNED' | translate:{ room: v.learnedLabel } }}</div>
        <div *ngIf="v.facts" class="kv-sub">{{ v.facts }}</div>
        <div *ngIf="v.tableNote" class="kv-sub text-warning-emphasis">{{ v.tableNote }}</div>
        <div *ngIf="v.bookNote" class="kv-sub text-warning-emphasis">{{ v.bookNote }}</div>
      </div>
      <div class="kv-c-cand">
        <span class="kv-lbl">{{ 'ADMIN_KNX_VALVES_COL_CANDIDATES' | translate }}</span>
        <div class="kv-cands">
          <div *ngFor="let c of v.cands; trackBy: trackCand" class="kv-cand" [class.on]="c.assigned" [title]="c.title">
            <span class="text-truncate">{{ c.label }}</span>
            <span class="kv-gauge"><span [style.width.%]="c.bar"></span></span>
            <span class="kv-score">{{ c.score }}</span>
          </div>
          <span *ngIf="!v.cands.length" class="kv-sub">{{ 'ADMIN_KNX_VALVES_NO_EVIDENCE' | translate }}</span>
        </div>
      </div>
      <div class="kv-c-acts">
        <div class="kv-acts">
          <button type="button" class="btn btn-sm btn-outline-success"
                  [disabled]="busy[v.ga] || !v.roomId || v.status === 'confirmed' || v.status === 'verified'"
                  [title]="'ADMIN_KNX_VALVES_CONFIRM_HINT' | translate"
                  (click)="confirm(v)">✓ {{ 'ADMIN_KNX_VALVES_CONFIRM' | translate }}</button>
          <select #sel class="form-select form-select-sm" [disabled]="busy[v.ga] || !(b.rooms.length || b.others.length)"
                  [attr.aria-label]="'ADMIN_KNX_VALVES_PICK' | translate" (change)="pick(v, sel)">
            <option value="">{{ 'ADMIN_KNX_VALVES_PICK' | translate }}</option>
            <option *ngFor="let r of b.rooms; trackBy: trackRoom" [value]="r.id">{{ r.label }}</option>
            <option [value]="none">{{ 'ADMIN_KNX_VALVES_NO_ROOM' | translate }}</option>
            <!-- Rooms of the other floors: for a floor block that was derived wrongly -->
            <optgroup *ngIf="b.others.length" [label]="'ADMIN_KNX_VALVES_OTHER_FLOORS' | translate">
              <option *ngFor="let r of b.others" [value]="r.id">{{ r.label }}</option>
            </optgroup>
          </select>
          <button type="button" class="btn btn-sm btn-outline-secondary"
                  [disabled]="busy[v.ga] || !(v.override || v.status === 'confirmed')"
                  [title]="'ADMIN_KNX_VALVES_RESET_HINT' | translate"
                  (click)="reset(v)">↺ {{ 'ADMIN_KNX_VALVES_RESET' | translate }}</button>
          <span *ngIf="busy[v.ga]" class="spinner-border spinner-border-sm text-muted" role="status"></span>
        </div>
        <div *ngIf="postError[v.ga]" class="kv-sub text-danger mt-1">{{ 'ADMIN_KNX_ERROR' | translate:{ msg: postError[v.ga] } }}</div>
      </div>
    </div>
    <div *ngIf="!b.valves.length" class="kv-row"><span class="kv-hint" style="grid-column:1 / -1;">{{ 'ADMIN_KNX_VALVES_NO_VALVES' | translate }}</span></div>
  </section>
</div>
